/**
 * Frame-time instrumentation (2026-08-01 frame-drop pass).
 *
 * Opt-in via `?perf=1`. Chrome's own tracing is not available to the agents
 * driving this game headless, so the loop measures itself: a ring buffer of
 * total frame times plus per-phase accumulators around the render callback's
 * stages (rig, road+biomes, juice, audio, vehicles, render submit, hud).
 *
 * `renderer.render` on WebGPU RETURNS BEFORE THE GPU IS DONE — the "render"
 * phase measured here is CPU submit time, not GPU time. GPU cost shows up as
 * back-pressure in the NEXT frame's total, which is why the headline numbers
 * below are the total-frame percentiles, not the phase sums.
 *
 * Read it from the console as `__perf.report()`; the object is also exposed on
 * `window.__perf` so a driving agent can poll it.
 */

/** Phases measured inside the render callback. */
export const FRAME_PHASES = [
  "rig",
  "world",
  "juice",
  "audio",
  "vehicles",
  "submit",
  "hud",
] as const;
export type FramePhase = (typeof FRAME_PHASES)[number];

export interface FrameReport {
  frames: number;
  /** Wall-clock seconds covered by the buffer. */
  seconds: number;
  fps: number;
  p50: number;
  p95: number;
  p99: number;
  max: number;
  /** Frames longer than 20 ms / 33 ms (a dropped frame at 60 / 30 Hz). */
  over20: number;
  over33: number;
  /** Mean ms per phase over the buffer. */
  phases: Record<FramePhase, number>;
  /** The ten worst frames (ms), newest last. */
  worst: number[];
  /** Kick-correlated split (see {@link FrameStats.markKick}). */
  kicks: number;
  inKick: FrameSlice;
  outKick: FrameSlice;
  /** Spawn-correlated split (see {@link FrameStats.markSpawn}). */
  spawns: number;
  /** Half-width (ms) of the spawn window this report was computed at. */
  spawnWindowMs: number;
  inSpawn: FrameSlice;
  outSpawn: FrameSlice;
  /**
   * WRECK-LIFETIME split (2026-08-02 wreck-spike pass). A wreck outlives the
   * ±500 ms kick window by seconds — it flies, lands, skids, then despawns —
   * and the residual 47–70 ms frames were landing in that tail, invisible to
   * the kick split. Three disjoint buckets covering every recorded frame:
   *   inKickWindow — within ±KICK_WINDOW_MS of a kick,
   *   inWreck      — outside that, but inside some wreck's [kick, down] span,
   *   background   — neither (no wreck alive nearby).
   */
  wrecks: number;
  inKickWindow: FrameSlice;
  inWreck: FrameSlice;
  background: FrameSlice;
  /** Every frame over {@link SPIKE_MS}, attributed to the nearest fine marker. */
  spikes: SpikeInfo[];
  /** Moment markers recorded (see {@link FrameStats.markMoment}). */
  moments: number;
}

/** One slow frame, attributed. */
export interface SpikeInfo {
  /** Frame time (ms). */
  ms: number;
  /** Seconds into the recording (first recorded frame = 0). */
  at: number;
  /** Which lifetime bucket the frame fell in. */
  zone: "kick" | "wreck" | "bg";
  /** Nearest fine marker label, or "" when none was recorded. */
  nearest: string;
  /** Signed ms from that marker to the END of the spike frame (+ = after). */
  nearestDtMs: number;
  /** Per-phase ms for this frame (which stage of the render callback ate it). */
  phases: Record<FramePhase, number>;
}

/** Frames at or above this are listed individually in `report().spikes`. */
export const SPIKE_MS = 20;

/** Frame-time summary of a SUBSET of the buffer (kick window / everything else). */
export interface FrameSlice {
  frames: number;
  p50: number;
  p99: number;
  max: number;
  over20: number;
  over33: number;
}

/**
 * Half-width (ms) of the window around a kick that counts as "kick-correlated".
 * The playtest reported drops both just before and just after a kick, so the
 * window is symmetric.
 */
export const KICK_WINDOW_MS = 500;

/**
 * Half-width (ms) of the window around a vehicle SPAWN that counts as
 * "spawn-correlated" (2026-08-01 spawn-clone pass). Narrower than the kick
 * window because spawns are ~2/s: a wide window would swallow the whole run and
 * the split would say nothing. ±300 ms still covers the frame the spawn lands
 * on plus a couple of GC-tail frames either side.
 */
export const SPAWN_WINDOW_MS = 300;

/** Percentile of a COPY of the samples (the caller's array is left alone). */
export function percentile(sorted: readonly number[], q: number): number {
  if (sorted.length === 0) return 0;
  const i = Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * q)));
  return sorted[i];
}

/**
 * Fixed-capacity frame-time recorder. Zero allocation while recording: the
 * sample buffers are typed arrays sized once.
 */
export class FrameStats {
  private readonly cap: number;
  private readonly total: Float32Array;
  /** performance.now() at the END of each recorded frame (kick correlation). */
  private readonly stamp: Float64Array;
  /** Ring of kick timestamps (performance.now()). */
  private readonly kickAt: Float64Array;
  private kickN = 0;
  private kickHead = 0;
  /** Ring of vehicle-spawn timestamps (performance.now()). */
  private readonly spawnAt: Float64Array;
  private spawnN = 0;
  private spawnHead = 0;
  /**
   * WRECK LIFETIME SPANS. `wreckStart[i]`/`wreckEnd[i]` bound one wreck's life
   * (kick → kickedVehicleDown). An OPEN span has `end = Infinity`, so a wreck
   * still alive at report() time still claims its frames. Slot lookup is by
   * vehicle id (`wreckOpen[id]` = index of that id's open span, -1 if none),
   * because ids are recycled by the pool and two wrecks can overlap in time.
   */
  private readonly wreckStart: Float64Array;
  private readonly wreckEnd: Float64Array;
  private wreckN = 0;
  private readonly wreckOpen = new Int32Array(256).fill(-1);
  /** Ring of labeled fine markers (conversion / ground-hit / rest / despawn). */
  private readonly momentAt: Float64Array;
  private readonly momentLabel: string[];
  private momentN = 0;
  private momentHead = 0;
  private readonly phaseSamples: Record<FramePhase, Float32Array>;
  private n = 0;
  private head = 0;
  private phaseStart = 0;
  private frameStart = 0;
  /**
   * Half-width of the spawn correlation window, in ms. MUTABLE on purpose: at
   * ~4-5 spawns/s a fixed ±300 ms window swallows three quarters of the run and
   * the in/out split stops discriminating, so a bench session re-runs
   * `report()` at several widths over the SAME recorded frames
   * (`__perf.spawnWindowMs = 30; __perf.report()`).
   */
  spawnWindowMs = SPAWN_WINDOW_MS;
  private current: Record<FramePhase, number>;

  constructor(capacity = 20000, kickCapacity = 4096, spawnCapacity = 4096) {
    this.cap = capacity;
    this.total = new Float32Array(capacity);
    this.stamp = new Float64Array(capacity);
    this.kickAt = new Float64Array(kickCapacity);
    this.spawnAt = new Float64Array(spawnCapacity);
    this.wreckStart = new Float64Array(kickCapacity);
    this.wreckEnd = new Float64Array(kickCapacity);
    this.momentAt = new Float64Array(kickCapacity);
    this.momentLabel = new Array<string>(kickCapacity).fill("");
    this.phaseSamples = {} as Record<FramePhase, Float32Array>;
    this.current = {} as Record<FramePhase, number>;
    for (const p of FRAME_PHASES) {
      this.phaseSamples[p] = new Float32Array(capacity);
      this.current[p] = 0;
    }
  }

  /** Call at the top of the render callback. */
  beginFrame(now = performance.now()): void {
    this.frameStart = now;
    this.phaseStart = now;
  }

  /** Close the named phase (started at the previous mark) and open the next. */
  mark(phase: FramePhase, now = performance.now()): void {
    this.current[phase] = now - this.phaseStart;
    this.phaseStart = now;
  }

  /**
   * Record that a kick connected, at wall-clock `now`. Frames whose end stamp
   * falls within ±{@link KICK_WINDOW_MS} of any mark are reported separately,
   * which is what turns "it hitches around kicks" into a measurable statistic.
   */
  markKick(now = performance.now()): void {
    this.kickAt[this.kickHead] = now;
    this.kickHead = (this.kickHead + 1) % this.kickAt.length;
    if (this.kickN < this.kickAt.length) this.kickN++;
  }

  /**
   * Record that a vehicle spawned, at wall-clock `now`. Mirrors
   * {@link markKick} with its own ±{@link SPAWN_WINDOW_MS} window, so a spawn
   * that costs a frame shows up as a statistic ("in-spawn p99 vs out-of-spawn
   * p99") instead of a hunch.
   */
  markSpawn(now = performance.now()): void {
    this.spawnAt[this.spawnHead] = now;
    this.spawnHead = (this.spawnHead + 1) % this.spawnAt.length;
    if (this.spawnN < this.spawnAt.length) this.spawnN++;
  }

  /**
   * Open a WRECK LIFETIME span for `vehicleId` (called on the `kick` event, the
   * same instant as {@link markKick}). Frames inside the span but outside the
   * kick window land in `report().inWreck`.
   */
  markWreckStart(vehicleId: number, now = performance.now()): void {
    if (this.wreckN >= this.wreckStart.length) return;
    const i = this.wreckN++;
    this.wreckStart[i] = now;
    this.wreckEnd[i] = Infinity;
    if (vehicleId >= 0 && vehicleId < this.wreckOpen.length) {
      this.wreckOpen[vehicleId] = i;
    }
  }

  /** Close the span opened for `vehicleId` (on `kickedVehicleDown`/despawn). */
  markWreckEnd(vehicleId: number, now = performance.now()): void {
    if (vehicleId < 0 || vehicleId >= this.wreckOpen.length) return;
    const i = this.wreckOpen[vehicleId];
    if (i < 0) return;
    this.wreckEnd[i] = now;
    this.wreckOpen[vehicleId] = -1;
  }

  /**
   * Record a labeled DISCRETE MOMENT (body conversion, first ground hit, rest,
   * despawn/recycle...). `report()` matches each spike frame to the nearest one
   * and reports the distance, which is what turns "spikes happen while a wreck
   * is alive" into "spikes happen 0 ms after the ground hit".
   */
  markMoment(label: string, now = performance.now()): void {
    this.momentAt[this.momentHead] = now;
    this.momentLabel[this.momentHead] = label;
    this.momentHead = (this.momentHead + 1) % this.momentAt.length;
    if (this.momentN < this.momentAt.length) this.momentN++;
  }

  /** Call at the bottom of the render callback. */
  endFrame(now = performance.now()): void {
    const i = this.head;
    this.stamp[i] = now;
    this.total[i] = now - this.frameStart;
    for (const p of FRAME_PHASES) {
      this.phaseSamples[p][i] = this.current[p];
      this.current[p] = 0;
    }
    this.head = (this.head + 1) % this.cap;
    if (this.n < this.cap) this.n++;
  }

  /** Drop everything recorded so far (used to skip the first warm frames). */
  reset(): void {
    this.n = 0;
    this.head = 0;
    this.kickN = 0;
    this.kickHead = 0;
    this.spawnN = 0;
    this.spawnHead = 0;
    this.wreckN = 0;
    this.wreckOpen.fill(-1);
    this.momentN = 0;
    this.momentHead = 0;
  }

  /** Whether `t` sits inside any wreck's [kick, down] lifetime span. */
  private inWreckSpan(t: number): boolean {
    for (let k = 0; k < this.wreckN; k++) {
      if (t >= this.wreckStart[k] && t <= this.wreckEnd[k]) return true;
    }
    return false;
  }

  /** Whether `t` (performance.now() ms) sits inside any kick's ±window. */
  private nearKick(t: number): boolean {
    for (let k = 0; k < this.kickN; k++) {
      const d = t - this.kickAt[k];
      if (d > -KICK_WINDOW_MS && d < KICK_WINDOW_MS) return true;
    }
    return false;
  }

  /** Whether `t` (performance.now() ms) sits inside any spawn's ±window. */
  private nearSpawn(t: number): boolean {
    for (let k = 0; k < this.spawnN; k++) {
      const d = t - this.spawnAt[k];
      if (d > -this.spawnWindowMs && d < this.spawnWindowMs) return true;
    }
    return false;
  }

  report(): FrameReport {
    const n = this.n;
    const samples: number[] = new Array(n);
    for (let i = 0; i < n; i++) samples[i] = this.total[i];
    const sorted = samples.slice().sort((a, b) => a - b);
    const sum = samples.reduce((a, b) => a + b, 0);
    const phases = {} as Record<FramePhase, number>;
    for (const p of FRAME_PHASES) {
      let s = 0;
      for (let i = 0; i < n; i++) s += this.phaseSamples[p][i];
      phases[p] = n ? s / n : 0;
    }
    const inK: number[] = [];
    const outK: number[] = [];
    const inS: number[] = [];
    const outS: number[] = [];
    // Three DISJOINT lifetime buckets + the individually attributed spikes.
    const zKick: number[] = [];
    const zWreck: number[] = [];
    const zBg: number[] = [];
    const spikes: SpikeInfo[] = [];
    const t0 = n ? this.stamp[0] : 0;
    for (let i = 0; i < n; i++) {
      const t = this.stamp[i];
      const ms = this.total[i];
      (this.nearKick(t) ? inK : outK).push(ms);
      (this.nearSpawn(t) ? inS : outS).push(ms);
      const zone: "kick" | "wreck" | "bg" = this.nearKick(t)
        ? "kick"
        : this.inWreckSpan(t)
          ? "wreck"
          : "bg";
      (zone === "kick" ? zKick : zone === "wreck" ? zWreck : zBg).push(ms);
      if (ms >= SPIKE_MS) {
        // Nearest fine marker to the END of this frame. The frame's own span is
        // [t - ms, t], so a marker inside it reports a small positive distance.
        let nearest = "";
        let bestDt = Infinity;
        for (let k = 0; k < this.momentN; k++) {
          const d = t - this.momentAt[k];
          if (Math.abs(d) < Math.abs(bestDt)) {
            bestDt = d;
            nearest = this.momentLabel[k];
          }
        }
        const ph = {} as Record<FramePhase, number>;
        for (const p of FRAME_PHASES) ph[p] = this.phaseSamples[p][i];
        spikes.push({
          ms,
          at: (t - t0) / 1000,
          zone,
          nearest,
          nearestDtMs: nearest ? bestDt : 0,
          phases: ph,
        });
      }
    }
    return {
      frames: n,
      seconds: sum / 1000,
      fps: sum > 0 ? (n * 1000) / sum : 0,
      p50: percentile(sorted, 0.5),
      p95: percentile(sorted, 0.95),
      p99: percentile(sorted, 0.99),
      max: sorted.length ? sorted[sorted.length - 1] : 0,
      over20: samples.filter((v) => v > 20).length,
      over33: samples.filter((v) => v > 33).length,
      phases,
      worst: sorted.slice(-10),
      kicks: this.kickN,
      inKick: slice(inK),
      outKick: slice(outK),
      spawns: this.spawnN,
      spawnWindowMs: this.spawnWindowMs,
      inSpawn: slice(inS),
      outSpawn: slice(outS),
      wrecks: this.wreckN,
      inKickWindow: slice(zKick),
      inWreck: slice(zWreck),
      background: slice(zBg),
      spikes,
      moments: this.momentN,
    };
  }
}

/** Summarise a subset of frame times. */
function slice(samples: number[]): FrameSlice {
  const sorted = samples.slice().sort((a, b) => a - b);
  return {
    frames: sorted.length,
    p50: percentile(sorted, 0.5),
    p99: percentile(sorted, 0.99),
    max: sorted.length ? sorted[sorted.length - 1] : 0,
    over20: sorted.filter((v) => v > 20).length,
    over33: sorted.filter((v) => v > 33).length,
  };
}
